import { useNavigate } from "@tanstack/react-router";
import { Building2, Check, LoaderCircle, QrCode, Search } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { useCustomerDirectoryQuery, useResolveRegistrationRequestMutation } from "@/api/query-hooks";
import type { CustomerDirectoryItem } from "@/api/types";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export function RegistrationRequestLookupPage() {
  const { t } = useTranslation(["registrationRequests", "common"]);
  const navigate = useNavigate({ from: "/registration-requests" });
  const resolve = useResolveRegistrationRequestMutation();
  const [locator, setLocator] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selected, setSelected] = useState<CustomerDirectoryItem | null>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const directory = useCustomerDirectoryQuery({ q: debouncedSearch, mode: "all", limit: 24 });
  const customers = useMemo(() => directory.data?.pages.flatMap((page) => page.items) ?? [], [directory.data]);

  useEffect(() => {
    const timeout = window.setTimeout(() => setDebouncedSearch(customerSearch.trim()), 300);
    return () => window.clearTimeout(timeout);
  }, [customerSearch]);
  useEffect(() => {
    const node = loadMoreRef.current;
    if (!node || !directory.hasNextPage || directory.isFetchingNextPage) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting)) void directory.fetchNextPage();
    }, { rootMargin: "240px 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [directory.fetchNextPage, directory.hasNextPage, directory.isFetchingNextPage]);

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    const value = locator.trim();
    if (!value) return;
    if (isCanonicalRequestID(value)) {
      setLocator("");
      void navigate({ to: "/registration-requests/$requestId", params: { requestId: value } });
      return;
    }
    if (!selected || selected.status !== "ACTIVE") return;
    resolve.mutate({ payload: value, clientId: selected.id }, {
      onSuccess: (result) => void navigate({ to: "/registration-requests/$requestId", params: { requestId: result.request.id } }),
      onSettled: () => setLocator("")
    });
  };

  return (
    <div className="grid gap-4">
      <div>
        <h2 className="flex items-center gap-2 text-lg font-semibold"><QrCode className="h-5 w-5" />{t("title")}</h2>
        <p className="mt-1 max-w-3xl text-sm text-muted-foreground">{t("lookupDescription")}</p>
      </div>
      <section className="rounded-md border bg-panel p-4">
        <form className="grid gap-4" onSubmit={submit}>
          <div className="grid gap-2">
            <Label htmlFor="registration-request-client-search">{t("clientLabel")}</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input id="registration-request-client-search" className="h-11 pl-9" value={customerSearch} onChange={(event) => {
                setCustomerSearch(event.target.value);
                setSelected(null);
              }} placeholder={t("clientPlaceholder")} autoComplete="off" />
            </div>
            {directory.isError ? <p role="alert" className="text-sm text-destructive">{t("clientLoadFailed")}</p> : null}
            {!directory.isError ? (
              <div className="max-h-80 overflow-y-auto rounded-md border" aria-label={t("clientResults")}>
                {customers.map((customer) => {
                  const active = customer.status === "ACTIVE";
                  const chosen = selected?.id === customer.id;
                  return (
                    <button key={customer.id} type="button" disabled={!active} onClick={() => setSelected(customer)} className="flex w-full min-w-0 items-center gap-3 border-b px-3 py-3 text-left last:border-b-0 hover:bg-accent disabled:cursor-not-allowed disabled:opacity-60" aria-pressed={chosen}>
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-muted">{chosen ? <Check className="h-4 w-4 text-primary" /> : <Building2 className="h-4 w-4" />}</span>
                      <span className="min-w-0 flex-1"><span className="block break-words font-medium">{customer.name}</span><span className="text-xs text-muted-foreground">{t("clientNumber", { number: customer.number })}</span></span>
                      <Badge variant={active ? "success" : "warning"}>{customer.status}</Badge>
                    </button>
                  );
                })}
                {!directory.isLoading && customers.length === 0 ? <p className="px-3 py-6 text-center text-sm text-muted-foreground">{t("clientEmpty")}</p> : null}
                <div ref={loadMoreRef} className="flex min-h-10 items-center justify-center text-xs text-muted-foreground">
                  {directory.isLoading || directory.isFetchingNextPage ? <><LoaderCircle className="mr-2 h-4 w-4 animate-spin" />{t("clientLoading")}</> : null}
                </div>
              </div>
            ) : null}
          </div>

          <div className="grid gap-2">
            <Label htmlFor="registration-request-locator">{t("locatorLabel")}</Label>
            <div className="flex flex-col gap-2 sm:flex-row">
              <Input id="registration-request-locator" autoComplete="off" value={locator} onChange={(event) => setLocator(event.target.value)} placeholder={t("locatorPlaceholder")} />
              <Button type="submit" disabled={!canResolve(locator, selected) || resolve.isPending}><Search className="h-4 w-4" />{t("resolve")}</Button>
            </div>
          </div>
          {resolve.isError ? <p role="alert" className="text-sm text-destructive">{t("lookupFailed")}</p> : null}
        </form>
      </section>
    </div>
  );
}

function isCanonicalRequestID(value: string) {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/.test(value);
}

function canResolve(locator: string, customer: CustomerDirectoryItem | null) {
  const value = locator.trim();
  if (!value) return false;
  if (isCanonicalRequestID(value)) return true;
  return customer?.status === "ACTIVE";
}
